import { createSlice } from '@reduxjs/toolkit';
import tripLegInventoryService from '../services/tripLegInventoryService';
import type {
  TripLegPackageDetail,
  TripLeg,
  TripLegPackage,
  TripServiceConfiguration,
} from './models/trip-leg.model';
import * as uiSlice from './ui.slice';
import type { TripLegPackageRejectReason } from './models/trip-leg-package-reject-reason.model';
import referenceService from '../services/referenceService';
import type { AppDispatch } from './store';
import { forkJoin } from 'rxjs';
import accountingService from '../services/accountingService';

interface TripInventoryState {
  offeredPackages: TripLegPackageDetail[];
  loadingOfferedPackages: boolean;
  loadedOfferedPackages: boolean;
  tripLegPackage: TripLegPackageDetail | null;
  loadingTripLegPackage: boolean;
  loadedTripLegPackage: boolean;
  tripServiceConfiguration: TripServiceConfiguration | null;
  loadingTripServiceConfiguration: boolean;
  loadedTripServiceConfiguration: boolean;
  acceptingTripPackage: boolean;
  acceptedTripPackage: boolean;
  rejectingTripPackage: boolean;
  rejectedTripPackage: boolean;
  tripLegPackageRejectReasons: TripLegPackageRejectReason[];
  loadingTripLegsTotalValue: boolean;
  loadedTripLegsTotalValue: boolean;
  tripLegsTotalValue: number | null;
  offeredTripLegEvent: boolean;
}

export const initialState: TripInventoryState = {
  offeredPackages: [],
  loadingOfferedPackages: false,
  loadedOfferedPackages: false,
  tripLegPackage: null,
  loadingTripLegPackage: false,
  loadedTripLegPackage: false,
  tripServiceConfiguration: {} as TripServiceConfiguration,
  loadingTripServiceConfiguration: false,
  loadedTripServiceConfiguration: false,
  acceptingTripPackage: false,
  acceptedTripPackage: false,
  rejectingTripPackage: false,
  rejectedTripPackage: false,
  tripLegPackageRejectReasons: [],
  loadingTripLegsTotalValue: false,
  loadedTripLegsTotalValue: false,
  tripLegsTotalValue: null,
  offeredTripLegEvent: false,
};

export const slice = createSlice({
  name: 'notification',
  initialState,
  reducers: {
    setOfferedPackages: (
      state: TripInventoryState,
      action: { type: string; payload: TripLegPackageDetail[] },
    ) => {
      state.offeredPackages = action.payload;
    },
    setLoadingOfferedPackages: (
      state: TripInventoryState,
      action: { type: string; payload: boolean },
    ) => {
      state.loadingOfferedPackages = action.payload;
    },
    setLoadedOfferedPackages: (
      state: TripInventoryState,
      action: { type: string; payload: boolean },
    ) => {
      state.loadedOfferedPackages = action.payload;
    },
    addPackage: (
      state: TripInventoryState,
      action: { type: string; payload: TripLegPackageDetail },
    ) => {
      const idx = state.offeredPackages.findIndex(
        (p) => p.packageUUID === action.payload.packageUUID,
      );
      if (idx >= 0) {
        state.offeredPackages[idx] = action.payload;
      } else {
        state.offeredPackages.push(action.payload);
      }
    },
    removePackage: (
      state: TripInventoryState,
      action: { type: string; payload: string },
    ) => {
      const packages = [...state.offeredPackages];
      const idx = packages.findIndex((p) => p.packageUUID === action.payload);
      if (idx >= 0) {
        packages.splice(idx, 1);
        state.offeredPackages = packages;
      }
    },
    setTripLegPackage: (
      state: TripInventoryState,
      action: { type: string; payload: TripLegPackageDetail | null },
    ) => {
      state.tripLegPackage = action.payload;
    },
    setLoadingTripLegPackage: (
      state: TripInventoryState,
      action: { type: string; payload: boolean },
    ) => {
      state.loadingTripLegPackage = action.payload;
    },
    setLoadedTripLegPackage: (
      state: TripInventoryState,
      action: { type: string; payload: boolean },
    ) => {
      state.loadedTripLegPackage = action.payload;
    },
    setTripServiceConfiguration: (
      state: TripInventoryState,
      action: { type: string; payload: TripServiceConfiguration | null },
    ) => {
      state.tripServiceConfiguration = action.payload;
    },
    setLoadingTripServiceConfiguration: (
      state: TripInventoryState,
      action: { type: string; payload: boolean },
    ) => {
      state.loadingTripServiceConfiguration = action.payload;
    },
    setLoadedTripServiceConfiguration: (
      state: TripInventoryState,
      action: { type: string; payload: boolean },
    ) => {
      state.loadedTripServiceConfiguration = action.payload;
    },
    setAcceptingTripPackage: (
      state: TripInventoryState,
      action: { type: string; payload: boolean },
    ) => {
      state.acceptingTripPackage = action.payload;
    },
    setAcceptedTripPackage: (
      state: TripInventoryState,
      action: { type: string; payload: boolean },
    ) => {
      state.acceptedTripPackage = action.payload;
    },
    setRejectingTripPackage: (
      state: TripInventoryState,
      action: { type: string; payload: boolean },
    ) => {
      state.rejectingTripPackage = action.payload;
    },
    setRejectedTripPackage: (
      state: TripInventoryState,
      action: { type: string; payload: boolean },
    ) => {
      state.rejectedTripPackage = action.payload;
    },
    setTripLegPackageRejectReasons: (
      state: TripInventoryState,
      action: { type: string; payload: TripLegPackageRejectReason[] },
    ) => {
      state.tripLegPackageRejectReasons = action.payload;
    },

    setLoadingTripLegsTotalValue: (
      state: TripInventoryState,
      action: { type: string; payload: boolean },
    ) => {
      state.loadingTripLegsTotalValue = action.payload;
    },
    setLoadedTripLegsTotalValue: (
      state: TripInventoryState,
      action: { type: string; payload: boolean },
    ) => {
      state.loadedTripLegsTotalValue = action.payload;
    },
    setTripLegsTotalValue: (
      state: TripInventoryState,
      action: { type: string; payload: number },
    ) => {
      state.tripLegsTotalValue = action.payload;
    },
    setOfferedTripLegEvent: (
      state: TripInventoryState,
      action: { type: string; payload: boolean },
    ) => {
      state.offeredTripLegEvent = action.payload;
    },
  },
});

export const { setOfferedPackages } = slice.actions;
export const { setLoadingOfferedPackages } = slice.actions;
export const { setLoadedOfferedPackages } = slice.actions;
export const { removePackage } = slice.actions;
export const { addPackage } = slice.actions;
export const { setTripLegPackage } = slice.actions;
export const { setLoadingTripLegPackage } = slice.actions;
export const { setLoadedTripLegPackage } = slice.actions;
export const { setTripServiceConfiguration } = slice.actions;
export const { setLoadingTripServiceConfiguration } = slice.actions;
export const { setLoadedTripServiceConfiguration } = slice.actions;

export const { setAcceptingTripPackage } = slice.actions;
export const { setAcceptedTripPackage } = slice.actions;
export const { setRejectingTripPackage } = slice.actions;
export const { setRejectedTripPackage } = slice.actions;
export const { setTripLegPackageRejectReasons } = slice.actions;

export const { setLoadingTripLegsTotalValue } = slice.actions;
export const { setLoadedTripLegsTotalValue } = slice.actions;
export const { setTripLegsTotalValue } = slice.actions;
export const { setOfferedTripLegEvent } = slice.actions;

export const getOfferedPackagesAsync =
  (providerID: number) => (dispatch: AppDispatch) => {
    dispatch(setLoadingOfferedPackages(true));
    dispatch(setLoadedOfferedPackages(false));
    dispatch(setOfferedPackages([]));
    tripLegInventoryService.getOfferedPackages(providerID).subscribe({
      next: (packages: TripLegPackageDetail[]) => {
        dispatch(setLoadingOfferedPackages(false));
        dispatch(setLoadedOfferedPackages(true));
        dispatch(
          setOfferedPackages(
            packages.sort((a, b) =>
              a.tripLegPackageOfferingExpirationDateTimeUTC.localeCompare(
                b.tripLegPackageOfferingExpirationDateTimeUTC,
              ),
            ),
          ),
        );
      },
      error: (err) => {
        dispatch(setLoadingOfferedPackages(false));
        dispatch(setLoadedOfferedPackages(false));
      },
    });
  };

export const getTripLegPackageAsync =
  (packageUUID: string) => (dispatch: AppDispatch) => {
    dispatch(setLoadingTripLegPackage(true));
    dispatch(setLoadedTripLegPackage(false));
    tripLegInventoryService.getTripLegPackage(packageUUID).subscribe({
      next: (tripLegPackage: TripLegPackageDetail) => {
        dispatch(setLoadingTripLegPackage(false));
        dispatch(setLoadedTripLegPackage(true));
        dispatch(setTripLegPackage(tripLegPackage));
      },
      error: (err) => {
        dispatch(setLoadingTripLegPackage(false));
        dispatch(setLoadedTripLegPackage(false));
        dispatch(setTripLegPackage(null));
      },
    });
  };

export const getTripServiceConfigurationAsync =
  () => (dispatch: AppDispatch) => {
    dispatch(setLoadingTripServiceConfiguration(true));
    dispatch(setLoadedTripServiceConfiguration(false));
    tripLegInventoryService.getServiceConfiguration().subscribe({
      next: (tripServiceConfiguration: TripServiceConfiguration) => {
        dispatch(setLoadingTripServiceConfiguration(false));
        dispatch(setLoadedTripServiceConfiguration(true));
        dispatch(setTripServiceConfiguration(tripServiceConfiguration));
      },
      error: (err) => {
        dispatch(setLoadingTripServiceConfiguration(false));
        dispatch(setLoadedTripServiceConfiguration(false));
        dispatch(setTripServiceConfiguration(null));
      },
    });
  };

export const acceptTripPackageAsync =
  (packageUUID: string) => (dispatch: AppDispatch) => {
    dispatch(setAcceptingTripPackage(true));
    dispatch(setAcceptedTripPackage(false));
    tripLegInventoryService.acceptTripPackage(packageUUID).subscribe({
      next: () => {
        dispatch(setAcceptingTripPackage(false));
        dispatch(setAcceptedTripPackage(true));
        dispatch(removePackage(packageUUID));
        dispatch(
          uiSlice.setSuccessMessage('snackbar.acceptTripPackageSuccessMessage'),
        );
      },
      error: (err) => {
        dispatch(setAcceptingTripPackage(false));
        dispatch(setAcceptedTripPackage(false));
        dispatch(
          uiSlice.setErrorMessage('snackbar.acceptTripPackageFailureMessage'),
        );
      },
    });
  };

export const rejectTripPackageAsync =
  (packageUUID: string, tripLegPackageStatusReasonBC: string) =>
  (dispatch: AppDispatch) => {
    dispatch(setRejectingTripPackage(true));
    dispatch(setRejectedTripPackage(false));
    tripLegInventoryService
      .rejectTripPackage(packageUUID, tripLegPackageStatusReasonBC)
      .subscribe({
        next: () => {
          dispatch(setRejectingTripPackage(false));
          dispatch(setRejectedTripPackage(true));
          dispatch(removePackage(packageUUID));
          dispatch(
            uiSlice.setSuccessMessage(
              'snackbar.rejectTripPackageSuccessMessage',
            ),
          );
        },
        error: (err) => {
          dispatch(setRejectingTripPackage(false));
          dispatch(setRejectedTripPackage(false));
          dispatch(
            uiSlice.setErrorMessage('snackbar.rejectTripPackageFailureMessage'),
          );
        },
      });
  };

export const getTier3TripLegPackageAsync =
  (packageUUID: string) => (dispatch: AppDispatch) => {
    dispatch(setLoadingTripLegPackage(true));
    dispatch(setLoadedTripLegPackage(false));
    tripLegInventoryService.getTier3TripLegPackage(packageUUID).subscribe({
      next: (tripLegPackage: TripLegPackageDetail) => {
        dispatch(setLoadingTripLegPackage(false));
        dispatch(setLoadedTripLegPackage(true));
        dispatch(setTripLegPackage(tripLegPackage));
      },
      error: (err) => {
        dispatch(setLoadingTripLegPackage(false));
        dispatch(setLoadedTripLegPackage(false));
        dispatch(setTripLegPackage(null));
      },
    });
  };

export const acceptTier3TripPackageAsync =
  (packageUUID: string) => (dispatch: AppDispatch) => {
    dispatch(setAcceptingTripPackage(true));
    dispatch(setAcceptedTripPackage(false));
    tripLegInventoryService.acceptTier3TripPackage(packageUUID).subscribe({
      next: () => {
        dispatch(setAcceptingTripPackage(false));
        dispatch(setAcceptedTripPackage(true));
        dispatch(removePackage(packageUUID));
        dispatch(
          uiSlice.setSuccessMessage('snackbar.acceptTripPackageSuccessMessage'),
        );
      },
      error: (err) => {
        dispatch(setAcceptingTripPackage(false));
        dispatch(setAcceptedTripPackage(false));
        dispatch(
          uiSlice.setErrorMessage('snackbar.acceptTripPackageFailureMessage'),
        );
      },
    });
  };

export const rejectTier3TripPackageAsync =
  (packageUUID: string, tripLegPackageStatusReasonBC: string) =>
  (dispatch: AppDispatch) => {
    dispatch(setRejectingTripPackage(true));
    dispatch(setRejectedTripPackage(false));
    tripLegInventoryService
      .rejectTier3TripPackage(packageUUID, tripLegPackageStatusReasonBC)
      .subscribe({
        next: () => {
          dispatch(setRejectingTripPackage(false));
          dispatch(setRejectedTripPackage(true));
          dispatch(removePackage(packageUUID));
          dispatch(
            uiSlice.setSuccessMessage(
              'snackbar.rejectTripPackageSuccessMessage',
            ),
          );
        },
        error: (err) => {
          dispatch(setRejectingTripPackage(false));
          dispatch(setRejectedTripPackage(false));
          dispatch(
            uiSlice.setErrorMessage('snackbar.rejectTripPackageFailureMessage'),
          );
        },
      });
  };

export const fetchTripLegPackageRejectReasonsAsync =
  () => (dispatch: AppDispatch) => {
    referenceService.getTripLegPackageRejectReasons().subscribe({
      next: (reasons) => {
        dispatch(setTripLegPackageRejectReasons(reasons));
      },
    });
  };

export const fetchTripLegsTotalValueAsync =
  (tripLegs: TripLeg[], providerID: number) => (dispatch: AppDispatch) => {
    dispatch(setLoadingTripLegsTotalValue(true));
    dispatch(setLoadedTripLegsTotalValue(false));

    forkJoin(
      tripLegs.map(({ passengerUUID, tripLegReferenceID }) =>
        accountingService.getTripLegPricings(passengerUUID, tripLegReferenceID),
      ),
    ).subscribe({
      next: (tripLegsPricings) => {
        const totalValue = tripLegsPricings.reduce((acc, { provider }) => {
          const providerPayableAmount = provider
            .filter((p) => p.transportationProviderID === providerID)
            .reduce((sum, { payableAmount }) => sum + Number(payableAmount), 0);

          return acc + providerPayableAmount;
        }, 0);

        dispatch(setLoadingTripLegsTotalValue(false));
        dispatch(setLoadedTripLegsTotalValue(true));
        dispatch(setTripLegsTotalValue(totalValue));
      },
      error: (err) => {
        dispatch(setLoadingTripLegsTotalValue(false));
        dispatch(setLoadedTripLegsTotalValue(false));
      },
    });
  };

export const selectOfferedPackages = (state: {
  tripPackageInventory: TripInventoryState;
}) => state.tripPackageInventory.offeredPackages;
export const selectLoadingOfferedPackages = (state: {
  tripPackageInventory: TripInventoryState;
}) => state.tripPackageInventory.loadingOfferedPackages;
export const selectLoadedOfferedPackages = (state: {
  tripPackageInventory: TripInventoryState;
}) => state.tripPackageInventory.loadedOfferedPackages;
export const selectTripLegPackage = (state: {
  tripPackageInventory: TripInventoryState;
}) => state.tripPackageInventory.tripLegPackage;
export const selectLoadingTripLegPackage = (state: {
  tripPackageInventory: TripInventoryState;
}) => state.tripPackageInventory.loadingTripLegPackage;
export const selectLoadedTripLegPackage = (state: {
  tripPackageInventory: TripInventoryState;
}) => state.tripPackageInventory.loadedTripLegPackage;
export const selectTripServiceConfiguration = (state: {
  tripPackageInventory: TripInventoryState;
}) => state.tripPackageInventory.tripServiceConfiguration;
export const selectLoadingTripServiceConfiguration = (state: {
  tripPackageInventory: TripInventoryState;
}) => state.tripPackageInventory.loadingTripServiceConfiguration;
export const selectLoadedTripServiceConfiguration = (state: {
  tripPackageInventory: TripInventoryState;
}) => state.tripPackageInventory.loadedTripServiceConfiguration;
export const selectAcceptingTripPackage = (state: {
  tripPackageInventory: TripInventoryState;
}) => state.tripPackageInventory.acceptingTripPackage;
export const selectAcceptedTripPackage = (state: {
  tripPackageInventory: TripInventoryState;
}) => state.tripPackageInventory.acceptedTripPackage;
export const selectRejectingTripPackage = (state: {
  tripPackageInventory: TripInventoryState;
}) => state.tripPackageInventory.rejectingTripPackage;
export const selectRejectedTripPackage = (state: {
  tripPackageInventory: TripInventoryState;
}) => state.tripPackageInventory.rejectedTripPackage;
export const selectTripLegPackageRejectReasons = (state: {
  tripPackageInventory: TripInventoryState;
}) => state.tripPackageInventory.tripLegPackageRejectReasons;
export const selectTripLegsTotalValue = (state: {
  tripPackageInventory: TripInventoryState;
}) => state.tripPackageInventory.tripLegsTotalValue;

export const selectOfferedTripLegEvent = (state: {
  tripPackageInventory: TripInventoryState;
}) => state.tripPackageInventory.offeredTripLegEvent;

export default slice.reducer;
